import { CheckOutlined } from '@ant-design/icons'
import { OrganizationContact, OrganizationMember } from '@prisma/client'
import { Link, useLocation } from '@remix-run/react'
import { Avatar, Button, Card, Flex, Skeleton, Tag, Typography } from 'antd'
import { Api } from '~/core/trpc'
import { OrganizationContactCommon } from '~/features/organizationContact/common'
import { Icon } from '~/features/shared/icons'
import { Toolbox } from '~/plugins/toolbox/shared'

type Props = {
  ticketId: string
  organizationMember: OrganizationMember
}

export const Thumbnail: React.FC<Props> = ({ ticketId }) => {
  const location = useLocation()

  const ticketQuery = Api.supportTicket.findUnique.useQuery(
    {
      where: {
        id: ticketId,
      },
      include: {
        conversation: {
          include: {
            participants: {
              where: {
                organizationContactId: {
                  not: null,
                },
              },
              include: {
                organizationContact: true,
              },
            },
            messages: {
              take: 1,
              orderBy: {
                createdAt: 'asc',
              },
            },
          },
        },
      },
    },
    { initialData: null },
  )

  if (!ticketQuery.data) {
    if (ticketQuery.isFetching) {
      return (
        <Flex
          vertical
          gap={8}
          style={{ maxWidth: '400px', minWidth: '400px', width: '100%' }}
        >
          <Skeleton.Button active shape="circle" />
          <Skeleton active paragraph={{ rows: 2 }} />
        </Flex>
      )
    }

    return (
      <Typography.Text type="secondary">
        Ticket does not exists anymore
      </Typography.Text>
    )
  }

  const ticket = ticketQuery.data
  const message = ticketQuery.data.conversation.messages[0]
  const contact = (ticketQuery.data.conversation.participants[0]
    ?.organizationContact ?? {}) as OrganizationContact
  const to = `${location.pathname}/tickets/${ticket.id}`

  const isClosed = ticket.status === 'CLOSED'

  const name = [contact.firstname, contact.lastname].filter(Boolean).join(' ')
  const date = Toolbox.Date.toNotionStyle(ticket.createdAt)

  const initials = Toolbox.Utility.stringToInitials(
    [contact.firstname, contact.lastname, contact.email]
      .filter(Boolean)
      .join(' '),
  )

  const color = OrganizationContactCommon.Helper.getColor(contact)

  return (
    <>
      <Link to={to}>
        <Card size="small">
          <Flex
            vertical
            style={{ maxWidth: '400px', minWidth: '400px', width: '100%' }}
            gap={8}
          >
            <Flex align="center">
              <Flex gap={8} align="center" flex={1}>
                <Avatar size="small" style={{ background: color }}>
                  {initials}
                </Avatar>
                <Typography.Text style={{ fontWeight: 500 }}>
                  {name}
                </Typography.Text>
              </Flex>

              <Flex>
                <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                  {date}
                </Typography.Text>
              </Flex>
            </Flex>

            <Typography.Paragraph
              type="secondary"
              style={{ fontSize: '12px' }}
              ellipsis={{ rows: 3 }}
            >
              {message?.content}
            </Typography.Paragraph>

            <Flex>
              <div>
                {isClosed && (
                  <Tag color="success" icon={<CheckOutlined />}>
                    {' '}
                    SOLVED
                  </Tag>
                )}
                {!isClosed && <Tag color="warning"> OPENED</Tag>}
              </div>

              <Button
                icon={<Icon name="arrow-long-right" />}
                iconPosition="end"
                size="small"
                variant="filled"
                color="default"
                block
              >
                View
              </Button>
            </Flex>
          </Flex>
        </Card>
      </Link>
    </>
  )
}
